/**
 * useTabUrlSync — a DetailPageTabs URL-sync app-szintű fele (react-router).
 *
 * A @kgc/ui `<DetailPageTabs>` router-agnosztikus (controlled `value`/`onValueChange`). Ez a hook
 * köti az aktív tabot egy URL-query-paramhoz (`?tab=…`) → deep-linkelhető + refresh-stabil tab-state.
 * (Ugyanaz a split mint a DataTable + useServerPagination.)
 *
 *   const [tab, setTab] = useTabUrlSync('tab', 'overview');
 *   <DetailPageTabs tabs={TABS} value={tab} onValueChange={setTab} />
 */
import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

export function useTabUrlSync(param: string, defaultTab: string): [string, (key: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const value = searchParams.get(param) ?? defaultTab;

  const onValueChange = useCallback(
    (key: string) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          // a default-tabra ne tegyünk query-paramot (tiszta URL)
          if (key === defaultTab) {
            next.delete(param);
          } else {
            next.set(param, key);
          }
          return next;
        },
        { replace: true }
      );
    },
    [param, defaultTab, setSearchParams]
  );

  return [value, onValueChange];
}
